/**
 * WhatChangedDialog — AI summary of what changed between two file versions
 * of the source Document behind a CAD drawing.
 *
 * Opens from the CAD ribbon's "What Changed?" button. Fetches the AI summary
 * from /api/v1/cad-agent/drawings/{drawing_id}/what-changed and renders:
 *   - Plain-English summary (2-3 sentences)
 *   - Bulleted list of significant changes
 *   - Collapsible "Raw diff" section showing the structured parametric diff
 *
 * Defaults to comparing the latest 2 versions; the backend handles the
 * selection. Future: let the user pick from/to versions.
 */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Stack,
    Alert,
    CircularProgress,
    Chip,
    Divider,
    Collapse,
    IconButton,
} from '@mui/material';
import {
    AutoAwesome,
    Close,
    ExpandMore,
    ExpandLess,
    Difference,
} from '@mui/icons-material';
import { api } from '../../services/api';

const WhatChangedDialog = ({ open, onClose, drawing }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [rawOpen, setRawOpen] = useState(false);

    useEffect(() => {
        if (!open || !drawing?.drawing_id) return;
        setLoading(true);
        setError('');
        setResult(null);
        setRawOpen(false);
        // Backend defaults to latest 2 versions when from/to are omitted.
        api.whatChanged(drawing.drawing_id)
            .then((res) => setResult(res.data || {}))
            .catch((err) => {
                const status = err?.response?.status;
                if (status === 400) {
                    setError(err?.response?.data?.detail || 'Cannot compare — need at least 2 versions');
                } else if (status === 404) {
                    setError('Drawing or version not found');
                } else {
                    setError(err?.response?.data?.detail || err?.message || 'Failed to load summary');
                }
            })
            .finally(() => setLoading(false));
    }, [open, drawing?.drawing_id]);

    const fromV = result?.from_version;
    const toV = result?.to_version;
    const summary = result?.summary;
    const changes = Array.isArray(result?.changes) ? result.changes : [];
    const parametricDifferences = result?.raw_diff?.parametric_differences || [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
                <Difference sx={{ color: '#8b5cf6' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" noWrap>What changed?</Typography>
                    {drawing?.name && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {drawing.name}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1.5 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">
                            Asking Claude to summarize the changes…
                        </Typography>
                    </Box>
                ) : error ? (
                    <Alert severity="warning">{error}</Alert>
                ) : result ? (
                    <Stack spacing={2}>
                        {/* Version pills */}
                        {(fromV || toV) && (
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Chip
                                    size="small"
                                    label={`From v${fromV?.version_number || '?'}`}
                                    variant="outlined"
                                />
                                <Typography variant="caption" color="text.secondary">→</Typography>
                                <Chip
                                    size="small"
                                    label={`To v${toV?.version_number || '?'}`}
                                    color="primary"
                                    variant="outlined"
                                />
                                {toV?.change_description && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        “{toV.change_description}”
                                    </Typography>
                                )}
                            </Stack>
                        )}

                        {/* AI summary */}
                        {summary && (
                            <Box sx={{ p: 2, bgcolor: '#f5f3ff', borderRadius: 2, borderLeft: '3px solid #8b5cf6' }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <AutoAwesome sx={{ fontSize: 16, color: '#8b5cf6' }} />
                                    <Typography variant="subtitle2" sx={{ color: '#6d28d9' }}>AI summary</Typography>
                                </Stack>
                                <Typography variant="body2">{summary}</Typography>
                            </Box>
                        )}

                        {/* Bulleted changes */}
                        {changes.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Key changes</Typography>
                                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                    {changes.map((c, idx) => (
                                        <Typography component="li" variant="body2" key={idx} sx={{ mb: 0.5 }}>
                                            {c}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Raw structured diff — collapsible. Shows parametric
                            properties + size/hash changes that produced the AI
                            summary. Useful for engineers who want to verify the
                            interpretation. */}
                        {parametricDifferences.length > 0 && (
                            <>
                                <Divider />
                                <Box>
                                    <Button
                                        size="small"
                                        onClick={() => setRawOpen((v) => !v)}
                                        endIcon={rawOpen ? <ExpandLess /> : <ExpandMore />}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Raw diff ({parametricDifferences.length} properties)
                                    </Button>
                                    <Collapse in={rawOpen}>
                                        <Box sx={{ mt: 1 }}>
                                            {parametricDifferences.map((d, idx) => (
                                                <Box
                                                    key={idx}
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 2,
                                                        py: 0.5,
                                                        borderBottom: idx < parametricDifferences.length - 1 ? '1px solid' : 'none',
                                                        borderColor: 'divider',
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                                                        {d.display_name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {String(d.old_value)} → <strong>{String(d.new_value)}</strong>
                                                    </Typography>
                                                    {d.delta_percent != null && (
                                                        <Chip
                                                            size="small"
                                                            label={`${d.delta_percent > 0 ? '+' : ''}${d.delta_percent}%`}
                                                            color={Math.abs(d.delta_percent) > 10 ? 'warning' : 'default'}
                                                            sx={{ height: 18, fontSize: '0.65rem' }}
                                                        />
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Collapse>
                                </Box>
                            </>
                        )}

                        {summary === undefined && changes.length === 0 && parametricDifferences.length === 0 && (
                            <Alert severity="info">No material differences detected between these versions.</Alert>
                        )}
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default WhatChangedDialog;
